import { type ReactNode, useLayoutEffect } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  type VirtualElement,
} from '@floating-ui/react';
import { Box } from '@mui/material';

import { LAUNCHER_ACTION_GAP, LAUNCHER_ACTION_SIZE } from './launcherPlacement';

interface Props {
  anchor: HTMLElement | VirtualElement | null;
  docked: boolean;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  id?: string;
  label?: string;
  interactive?: boolean;
  initialFocus?: number;
}

export function JotAnchoredPopover({
  anchor,
  docked,
  open,
  onClose,
  children,
  id,
  label,
  interactive = false,
  initialFocus = 0,
}: Props) {
  const { refs, floatingStyles, context } = useFloating({
    open: open && Boolean(anchor),
    onOpenChange: next => {
      if (!next) onClose();
    },
    placement: docked ? 'bottom-end' : 'top-start',
    strategy: 'fixed',
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: [
      offset(({ placement }) =>
        placement.startsWith('bottom')
          ? docked
            ? 22
            : LAUNCHER_ACTION_SIZE + LAUNCHER_ACTION_GAP + 8
          : 10,
      ),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift({ padding: 16 }),
      size({
        padding: 16,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
        },
      }),
    ],
  });
  useLayoutEffect(() => {
    refs.setReference(anchor);
  }, [anchor, refs]);
  const dismiss = useDismiss(context, { enabled: interactive, outsidePressEvent: 'pointerdown' });
  const { getFloatingProps } = useInteractions([dismiss]);
  if (!open || !anchor) return null;
  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={context}
        disabled={!interactive}
        modal={false}
        closeOnFocusOut={false}
        initialFocus={initialFocus}
        returnFocus
      >
        <Box
          ref={refs.setFloating}
          // Inline-style exception: Floating UI supplies live measured positioning; static styles stay in sx.
          style={floatingStyles}
          {...getFloatingProps()}
          id={id}
          role={interactive ? 'dialog' : undefined}
          aria-modal={interactive ? false : undefined}
          aria-label={label}
          tabIndex={interactive ? -1 : undefined}
          data-jot-popover={docked ? 'header' : 'floating'}
          sx={{
            zIndex: theme => theme.zIndex.modal - 1,
            maxWidth: 'calc(100vw - 32px)',
            overflowY: 'auto',
            borderRadius: 3,
            outline: 'none',
            boxShadow: 8,
          }}
        >
          {children}
        </Box>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
